/**
 * Schemes a Clarity attachment or image URL may use.
 *
 * `http://` is absent on purpose: these URLs are rendered as image sources and link targets inside an
 * authenticated app, so a plaintext fetch is a downgrade, not a convenience.
 *
 * `data:` is allowed only with an **image** media type, never bare. A bare `data:` prefix admits
 * `data:text/html;base64,…` and `data:text/html,<script>…</script>`, which execute in the embedding
 * origin — the same origin that holds the Clarity Supabase session token. Scheme alone is not a
 * sufficient allowlist for `data:`; scheme plus media type is.
 */
const SAFE_URL_PREFIXES: readonly string[] = ['https://', 'data:image/'];

/**
 * True when a URL may be rendered — `https://`, or a `data:` URL carrying an image.
 *
 * Rejects `javascript:`, `file://`, `blob:`, plain `http://`, protocol-relative URLs, non-image
 * `data:` payloads and the empty string. The check is a prefix test on the lowercased, trimmed value
 * rather than a `URL` parse: a parse accepts `javascript:alert(1)` as a perfectly valid URL, so
 * parsing would have to be followed by this same allowlist anyway.
 *
 * Ported from `isSafeUrl` in Clarity `apps/web/src/shared/utils/security.ts`, with one **deliberate
 * behaviour change**: theirs allows any `data:` media type. Carrying that into a library every
 * `ui-clarity-*` and `data-access-clarity-*` library sits above would be porting a stored-XSS vector,
 * so the media type is pinned here. A caller that genuinely needs another `data:` type adds it to
 * {@link SAFE_URL_PREFIXES} explicitly, with a test.
 *
 * The DOMPurify-based functions beside it in Clarity were **not** ported — see the README.
 *
 * Two other allowlists exist in this workspace today (`SAFE_HREF_PREFIXES` in `ui-clarity-markdown`,
 * `SAFE_MEETING_URL_PREFIXES` in `ui-clarity-pulse`), and both admit `http://`. This library sits
 * below both and is the right home to collapse them into — but the collapse is a behaviour change for
 * those call sites, not a refactor, and belongs to the boundary that owns them.
 */
export function isSafeUrl(url: string): boolean {
	if (!url) {
		return false;
	}

	const normalized = url.trim().toLowerCase();

	return SAFE_URL_PREFIXES.some(prefix => normalized.startsWith(prefix));
}
